// Google Tag Manager loader, gated behind cookie consent

let gtmLoaded = false;
let gtmContainerId = null;

export function isGtmLoaded() {
  return gtmLoaded;
}

export function loadGTM(containerId, dataLayerName = 'dataLayer') {
  if (gtmLoaded) return true;
  if (!containerId) {
    console.warn('GTM container ID not provided. Skipping GTM load.');
    return false;
  }

  // Validate containerId format to prevent injection attacks
  if (typeof containerId !== 'string' || !/^GTM-[A-Z0-9]+$/i.test(containerId)) {
    console.error('Invalid GTM container ID format. Expected GTM-XXXXXXX');
    return false;
  }

  // Validate dataLayerName to prevent property injection
  if (typeof dataLayerName !== 'string' || !/^[a-zA-Z_$][a-zA-Z0-9_$]*$/.test(dataLayerName)) {
    console.error('Invalid dataLayer name. Must be a valid JavaScript identifier.');
    return false;
  }

  // Prevent accessing prototype or built-in window properties
  if (Object.prototype.hasOwnProperty.call(window, dataLayerName) && dataLayerName in window && !Array.isArray(window[dataLayerName])) {
    console.error('Invalid dataLayer name. Property already exists on window and is not an array.');
    return false;
  }

  try {
    gtmContainerId = containerId;

    // Initialize dataLayer and push gtm.start
    const w = window;
    w[dataLayerName] = w[dataLayerName] || [];
    w[dataLayerName].push({ 'gtm.start': Date.now(), event: 'gtm.js' });

    // Inject GTM script
    const j = document.createElement('script');
    j.async = true;
    j.src = `https://www.googletagmanager.com/gtm.js?id=${encodeURIComponent(containerId)}${dataLayerName !== 'dataLayer' ? `&l=${encodeURIComponent(dataLayerName)}` : ''}`;

    // Insert script at the end of head or beginning of body
    const target = document.head || document.body;
    if (!target) {
      throw new Error('Cannot inject GTM script: document.head and document.body are not available');
    }
    target.insertBefore(j, target.firstChild);

    gtmLoaded = true;
    return true;
  } catch (e) {
    console.error('Failed to load GTM:', e);
    return false;
  }
}

export function pushToDataLayer(obj, dataLayerName = 'dataLayer') {
  // Validate dataLayerName to prevent property injection
  if (typeof dataLayerName !== 'string' || !/^[a-zA-Z_$][a-zA-Z0-9_$]*$/.test(dataLayerName)) {
    console.error('Invalid dataLayer name. Must be a valid JavaScript identifier.');
    return false;
  }

  // Prevent accessing prototype or built-in window properties
  if (Object.prototype.hasOwnProperty.call(window, dataLayerName) && dataLayerName in window && !Array.isArray(window[dataLayerName])) {
    console.error('Invalid dataLayer name. Property already exists on window and is not an array.');
    return false;
  }

  try {
    const w = window;
    w[dataLayerName] = w[dataLayerName] || [];
    w[dataLayerName].push(obj);
    return true;
  } catch (e) {
    console.error('Failed to push to dataLayer:', e);
    return false;
  }
}

export function removeGTM(dataLayerName = 'dataLayer') {
  // Validate dataLayerName to prevent property injection
  if (typeof dataLayerName !== 'string' || !/^[a-zA-Z_$][a-zA-Z0-9_$]*$/.test(dataLayerName)) {
    console.error('Invalid dataLayer name. Must be a valid JavaScript identifier.');
    return false;
  }

  // Prevent accessing prototype or built-in window properties
  if (Object.prototype.hasOwnProperty.call(window, dataLayerName) && dataLayerName in window && !Array.isArray(window[dataLayerName])) {
    console.error('Invalid dataLayer name. Property already exists on window and is not an array.');
    return false;
  }

  try {
    // Remove GTM script tag(s)
    const scripts = document.querySelectorAll('script[src*="googletagmanager.com/gtm.js"]');
    scripts.forEach((s) => s.parentNode && s.parentNode.removeChild(s));

    // Remove noscript iframe if present in DOM
    const iframes = document.querySelectorAll('iframe[src*="googletagmanager.com/ns.html"]');
    iframes.forEach((i) => i.parentNode && i.parentNode.removeChild(i));

    // Clear GTM globals
    if (window.google_tag_manager) {
      try {
        delete window.google_tag_manager;
      } catch (error) {
        console.warn('Could not delete google_tag_manager:', error);
        window.google_tag_manager = undefined;
      }
    }

    // Reset dataLayer
    if (window[dataLayerName]) {
      try {
        window[dataLayerName].length = 0;
      } catch (error) {
        console.warn('Could not reset dataLayer, reinitializing:', error);
        window[dataLayerName] = [];
      }
    }

    gtmLoaded = false;
    gtmContainerId = null;
    return true;
  } catch (e) {
    console.error('Failed to remove GTM:', e);
    return false;
  }
}
